import { gettext } from '@eventbrite/i18n';
import { css } from 'emotion';
import React from 'react';
import ProgressIndicator from '../../progressIndicator/ProgressIndicator';
import { useTheme } from '../../theme-context';
import { IAddon, ISummaryLine, ITicket } from '../types';
import AddonCheckoutLine from './addon-checkout-line';
import OrderSummaryCheckoutLine from './order-summary-checkout-line';
import TicketCheckoutLine from './ticket-checkout-line';
import TicketCheckoutLineHeader from './ticket-checkout-line-header';

interface ITicketSection {
    tickets: Array<ITicket>;
    removeTicketAction: (_: any) => void;
}

const TicketSection = ({ tickets, removeTicketAction }: ITicketSection) => {
    const { getFont } = useTheme();

    return tickets.length ? (
        <div data-testid="tickets-section">
            <div
                className={css({
                    fontFamily: getFont('desktop/body/medium/bold').fontFamily,
                    fontWeight: getFont('desktop/body/medium/bold').fontWeight,
                    fontSize: getFont('desktop/body/medium/bold').fontSize,
                    lineHeight: `${
                        getFont('desktop/body/medium/bold').lineHeightPx
                    }px`,
                })}
            >
                <p
                    className={css({
                        margin: '16px 0px',
                        fontFamily: getFont('desktop/body/large').fontFamily,
                    })}
                >
                    {gettext('Tickets')}
                </p>
                <div
                    className={css({
                        display: 'grid',
                        gridTemplateColumns: '30% 20% repeat(4, auto)',
                        gridTemplateRows: `auto repeat(${tickets.length}, 25px)`,
                        gridGap: '4px',
                    })}
                >
                    <TicketCheckoutLineHeader />
                    {tickets.map(
                        ({
                            id = '',
                            row = '',
                            seat = '',
                            section = '',
                            tier = '',
                            variantId = '',
                            variantName = '',
                        }) => {
                            const content = (
                                <TicketCheckoutLine
                                    key={id}
                                    ticket={{
                                        id,
                                        row,
                                        seat,
                                        section,
                                        tier,
                                        variantId,
                                        variantName,
                                    }}
                                    removeTicketAction={removeTicketAction}
                                />
                            );

                            return content;
                        },
                    )}
                </div>
            </div>
        </div>
    ) : null;
};

interface IAddonSection {
    addons: Array<IAddon>;
    setAddonQuantityAction: () => void;
    syncCart: () => void;
}

const AddonSection = ({
    addons,
    setAddonQuantityAction,
    syncCart,
}: IAddonSection) => {
    const { getFont } = useTheme();

    return addons.length ? (
        <div data-testid="addons-section">
            <p
                className={css({
                    marginTop: '16px',
                    fontFamily: getFont('desktop/body/medium/bold').fontFamily,
                    fontWeight: getFont('desktop/body/medium/bold').fontWeight,
                    fontSize: getFont('desktop/body/medium/bold').fontSize,
                    lineHeight: `${
                        getFont('desktop/body/medium/bold').lineHeightPx
                    }px`,
                })}
            >
                {gettext('Add-ons')}
            </p>
            {addons.map((addon) => (
                <AddonCheckoutLine
                    key={addon.id}
                    addon={addon}
                    setAddonQuantityAction={setAddonQuantityAction}
                    syncCart={syncCart}
                />
            ))}
        </div>
    ) : null;
};

interface ISummarySection {
    summaryLines: ISummaryLine[];
    currencySymbol?: string;
    setOrderSummaryTicketPriceAction: (
        ticketClassId: string,
        value: string,
    ) => void;
    syncCart: () => void;
}

const SummarySection = ({
    summaryLines,
    currencySymbol,
    setOrderSummaryTicketPriceAction,
    syncCart,
}: ISummarySection) => {
    const { getFont } = useTheme();

    return (
        <div data-testid="summary-section">
            <p
                className={css({
                    marginTop: '25px',
                    fontFamily: getFont('desktop/body/medium/bold').fontFamily,
                    fontWeight: getFont('desktop/body/medium/bold').fontWeight,
                    fontSize: getFont('desktop/body/medium/bold').fontSize,
                    lineHeight: `${
                        getFont('desktop/body/medium/bold').lineHeightPx
                    }px`,
                })}
            >
                {gettext('Order Summary')}
            </p>
            {summaryLines.map(
                ({
                    ticketClassId,
                    tier = '',
                    quantity = '',
                    name = '',
                    totalPrice = '',
                    ticketPriceNumber = '',
                }) => {
                    const content = (
                        <OrderSummaryCheckoutLine
                            key={ticketClassId}
                            summaryLine={{
                                ticketClassId,
                                tier,
                                quantity,
                                name,
                                totalPrice,
                                ticketPriceNumber,
                            }}
                            currencySymbol={currencySymbol}
                            setOrderSummaryTicketPriceAction={
                                setOrderSummaryTicketPriceAction
                            }
                            syncCart={syncCart}
                        />
                    );

                    return content;
                },
            )}
        </div>
    );
};

interface ITotalSection {
    totalPriceInfo: {
        price: string;
        currency: string;
    };
    isLoading?: boolean;
}

const TotalSection = ({ totalPriceInfo, isLoading }: ITotalSection) => {
    const { price, currency } = totalPriceInfo;
    const { getFont } = useTheme();

    return (
        <div
            data-testid="total-section"
            className={css({
                alignSelf: 'self-end',
                bottom: '0px',
                width: '100%',
                margin: '0px 0px 1px 1px',
                boxShadow:
                    '0 1px 10px 0 rgba(40,44,53,0.1), 0 1px 0px 0 rgba(40,28,53,0.1)',
            })}
        >
            <div
                className={css({
                    padding: '24px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, auto)',
                })}
            >
                <p
                    className={css({
                        fontFamily: getFont('desktop/body/medium/bold')
                            .fontFamily,
                        fontWeight: getFont('desktop/body/medium/bold')
                            .fontWeight,
                        fontSize: getFont('desktop/body/medium/bold').fontSize,
                        lineHeight: `${
                            getFont('desktop/body/medium/bold').lineHeightPx
                        }px`,
                    })}
                >
                    {gettext('Total')}
                </p>
                <div
                    className={css({
                        justifySelf: 'self-end',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, auto)',
                    })}
                >
                    {isLoading ? (
                        <div
                            data-testid="total-loading"
                            className={css({
                                padding: '8px',
                            })}
                        >
                            <ProgressIndicator style="gradient" />
                        </div>
                    ) : (
                        <>
                            <p
                                className={css({
                                    fontFamily: getFont(
                                        'desktop/body/medium/bold',
                                    ).fontFamily,
                                    fontWeight: getFont(
                                        'desktop/body/medium/bold',
                                    ).fontWeight,
                                    fontSize: getFont(
                                        'desktop/body/medium/bold',
                                    ).fontSize,
                                    lineHeight: `${
                                        getFont('desktop/body/medium/bold')
                                            .lineHeightPx
                                    }px`,
                                })}
                            >
                                {currency}
                            </p>
                            <p
                                className={css({
                                    fontFamily: getFont(
                                        'desktop/body/medium/bold',
                                    ).fontFamily,
                                    fontWeight: getFont(
                                        'desktop/body/medium/bold',
                                    ).fontWeight,
                                    fontSize: getFont(
                                        'desktop/body/medium/bold',
                                    ).fontSize,
                                    lineHeight: `${
                                        getFont('desktop/body/medium/bold')
                                            .lineHeightPx
                                    }px`,
                                })}
                            >
                                {price}
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

interface ICheckoutPanel {
    tickets: Array<ITicket>;
    addons: Array<IAddon>;
    summaryLines: Array<ISummaryLine>;
    syncCart: () => void;
    totalPriceInfo: {
        price: string;
        currency: string;
    };
    removeTicketAction: (_: any) => void;
    setAddonQuantityAction: () => void;
    setOrderSummaryTicketPriceAction: (
        ticketClassId: string,
        value: string,
    ) => void;
    isLoading?: boolean;
    currencySymbol?: string;
}

const CheckoutPanel = ({
    tickets = [],
    addons = [],
    summaryLines,
    removeTicketAction,
    setAddonQuantityAction,
    setOrderSummaryTicketPriceAction,
    syncCart,
    totalPriceInfo,
    isLoading,
    currencySymbol = '',
}: ICheckoutPanel) => {
    const { getFont, styles } = useTheme();

    return (
        <div
            data-testid="checkout-panel"
            className={css({
                '@keyframes slide-panel': {
                    from: {
                        right: '-350px',
                    },
                    to: {
                        right: 0,
                    },
                },
                animation: 'slide-panel 0.4s linear',
                transition: 'opacity 0.6s',
                opacity: 1,
                right: 0,
                height: '100%',
                width: '350px',
                position: 'absolute',
                overflow: 'hidden',
                display: 'grid',
                gridTemplateColumns: '1fr',
                fontFamily: getFont('desktop/body/large').fontFamily,
                backgroundColor: styles.colors['brand-white'].color,
            })}
        >
            <div
                className={css({
                    overflowY: 'scroll',
                })}
            >
                <div
                    className={css({
                        margin: '24px 24px 0px 24px',
                    })}
                >
                    <TicketSection
                        tickets={tickets}
                        removeTicketAction={removeTicketAction}
                    />
                    <AddonSection
                        addons={addons}
                        setAddonQuantityAction={setAddonQuantityAction}
                        syncCart={syncCart}
                    />
                    <SummarySection
                        summaryLines={summaryLines}
                        currencySymbol={currencySymbol}
                        setOrderSummaryTicketPriceAction={
                            setOrderSummaryTicketPriceAction
                        }
                        syncCart={syncCart}
                    />
                </div>
            </div>
            <TotalSection
                totalPriceInfo={totalPriceInfo}
                isLoading={isLoading}
            />
        </div>
    );
};

export default CheckoutPanel;
